/**
 * Reports > Bounces panel — the `Blotter` wired to `GET /reports/bounces`.
 *
 * Mirrors `RedemptionsPanel.tsx`: a `makeUseBouncesData` factory reports `isError` and
 * `filterOptions`/`unfilteredTotal` upward through `useEffect`, because `BlotterData` has no
 * channel for any of them and `Blotter.tsx` is shared by 11 surfaces and never changes for one.
 *
 * Two deliberate differences from that mould: no ColumnConfigPopover (the columns are fixed),
 * and `csvColumns` passed explicitly so the export carries the data columns and never Action.
 */
import MarkEmailReadOutlined from "@mui/icons-material/MarkEmailReadOutlined";
import { Alert, Button, Snackbar } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { fetchBounces } from "@/api/reportsBounces";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import type { AppliedFilter } from "@/components/BlotterFilterBar";
import { buildBounceColumns, buildBounceCsvColumns } from "@/components/reports/bouncesColumns";
import FixBouncedEmailModal from "@/components/reports/FixBouncedEmailModal";
import WidgetEmptyState from "@/components/WidgetEmptyState";
import {
  BOUNCES_TEXT,
  DEFAULT_SORT_COLUMN,
  DEFAULT_SORT_DIR,
  FILTER_FIELD_IDS,
  FILTER_LABELS,
  MIN_WIDTH,
  PAGE_SIZE,
} from "@/constants/reportsBounces";
import { usePermissions } from "@/hooks/usePermissions";
import { BOUNCES_QUERY_KEY, useReportsBounces } from "@/hooks/useReportsBounces";
import type { BounceRow, BouncesFilterOptions } from "@/interfaces/reportsBounces";
import { toBouncesListParams, toBouncesSortBy } from "@/utils/reportsBounces-helper";
import { pickEmptyMessage } from "@/utils/reportsRedemptions-helper";

/**
 * Builds the bounces blotter's data hook — a factory rather than a plain module-level hook, same
 * reason as `RedemptionsPanel.makeUseRedemptionsData`: `BlotterData` cannot carry an error flag or
 * the filter option lists, and both ride on the list response itself.
 */
function makeUseBouncesData(
  onError: (failed: boolean) => void,
  onFilterOptions: (options: BouncesFilterOptions) => void,
  onUnfilteredTotal: (total: number) => void,
) {
  return function useBouncesData(q: {
    search: string;
    filters: AppliedFilter[];
    sortBy: string;
    sortDir: "asc" | "desc";
    page: number;
    limit: number;
  }): BlotterData<BounceRow> {
    const { data, isLoading, isError } = useReportsBounces({
      ...toBouncesListParams(q),
      sortBy: toBouncesSortBy(q.sortBy),
      sortDir: q.sortDir,
      // Only the table's own request asks for them; the CSV export path below does not.
      includeFilterOptions: true,
    });

    // `onError`/`onFilterOptions`/`onUnfilteredTotal` are deliberately not dependencies: they are
    // the factory's parameters, not reactive values, and React re-runs the newest closure whenever
    // the watched value changes — so they are never stale (Biome's useExhaustiveDependencies
    // rejects outer-scope bindings here).
    useEffect(() => {
      onError(isError);
    }, [isError]);

    const filterOptions = data?.filterOptions;
    useEffect(() => {
      if (filterOptions) onFilterOptions(filterOptions);
    }, [filterOptions]);

    const unfilteredTotal = data?.unfilteredTotal;
    useEffect(() => {
      if (unfilteredTotal !== undefined) onUnfilteredTotal(unfilteredTotal);
    }, [unfilteredTotal]);

    return {
      data: data?.data ?? [],
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 1,
      isLoading,
    };
  };
}

export default function BouncesPanel() {
  const [listFailed, setListFailed] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);
  const [filterOptions, setFilterOptions] = useState<BouncesFilterOptions | null>(null);
  const [unfilteredTotal, setUnfilteredTotal] = useState<number | undefined>(undefined);
  const [fixRow, setFixRow] = useState<BounceRow | null>(null);
  const [resentIds, setResentIds] = useState<ReadonlySet<number>>(() => new Set<number>());
  const [toast, setToast] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { can } = usePermissions();

  /** Gates the whole Action control: no `send` on CAMPAIGN, no Fix on any row. */
  const canSend = can("send", "CAMPAIGN");

  /** Retry invalidates the whole bounces list branch; `isError` clears itself on success. */
  const handleRetry = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: BOUNCES_QUERY_KEY });
  }, [queryClient]);

  /** The local `resentIds` override shows "Resent" right away, before the refetch brings `resentAt`. */
  const handleResent = useCallback(
    (row: BounceRow, newEmail: string) => {
      setResentIds((previous) => new Set(previous).add(row.id));
      setToast(BOUNCES_TEXT.resendToast(newEmail));
      setFixRow(null);
      void queryClient.invalidateQueries({ queryKey: BOUNCES_QUERY_KEY });
    },
    [queryClient],
  );

  const useBouncesData = makeUseBouncesData(setListFailed, setFilterOptions, setUnfilteredTotal);

  const config: BlotterConfig<BounceRow> = {
    searchPlaceholder: BOUNCES_TEXT.searchPlaceholder,
    itemNoun: BOUNCES_TEXT.itemNoun,
    emptyMessage: BOUNCES_TEXT.emptyMessage,
    minWidth: MIN_WIDTH,
    pageSize: PAGE_SIZE,
    defaultSortKey: DEFAULT_SORT_COLUMN,
    defaultSortDir: DEFAULT_SORT_DIR,
    rowKey: (r) => r.id,
    useData: useBouncesData,
    columns: buildBounceColumns({ canSend, resentIds, onFix: setFixRow }),
    filterFields: [
      {
        id: FILTER_FIELD_IDS.campaign,
        label: FILTER_LABELS.campaign,
        options: filterOptions?.campaigns ?? [],
      },
      {
        id: FILTER_FIELD_IDS.recipientName,
        label: FILTER_LABELS.recipientName,
        options: [],
        kind: "text",
      },
      {
        id: FILTER_FIELD_IDS.recipientEmail,
        label: FILTER_LABELS.recipientEmail,
        options: [],
        kind: "text",
      },
    ],
    exportFilename: BOUNCES_TEXT.csvFilename,
    // Explicit rather than derived from the visible columns: the Action cell is a control, not a
    // value, and has no text a spreadsheet could carry.
    csvColumns: buildBounceCsvColumns(),
    // Same params as the table, so the CSV covers every filtered row across every page. No
    // `includeFilterOptions`: the walk doesn't need the campaign dropdown reloaded per page.
    exportFetch: async (q) => {
      const res = await fetchBounces(toBouncesListParams(q));
      return { data: res.data, totalPages: res.totalPages };
    },
    onExportError: () => setExportFailed(true),
  };

  // Only the absolute empty hides the controls. A filtered-out empty keeps the whole bar on screen
  // (`emptyMessage` above) — taking the search away would trap the user inside their own filter.
  if (pickEmptyMessage({ unfilteredTotal, isError: listFailed }) === "none-yet") {
    return (
      <div className="flex border border-smoke rounded-[10px] bg-white p-4">
        <WidgetEmptyState icon={<MarkEmailReadOutlined />} title={BOUNCES_TEXT.emptyTitle} />
      </div>
    );
  }

  return (
    <>
      {/* Above the Blotter, never in place of it: replacing the table would throw away the
          search, filters, sort and page the user is retrying with. */}
      {listFailed && (
        <Alert
          severity="error"
          role="alert"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              {BOUNCES_TEXT.retry}
            </Button>
          }
        >
          {BOUNCES_TEXT.listError}
        </Alert>
      )}
      {exportFailed && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }} onClose={() => setExportFailed(false)}>
          {BOUNCES_TEXT.exportError}
        </Alert>
      )}
      <Blotter config={config} />
      {fixRow && (
        <FixBouncedEmailModal
          row={fixRow}
          onClose={() => setFixRow(null)}
          onResent={handleResent}
        />
      )}
      <Snackbar
        open={toast !== null}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setToast(null)}>
          {toast ?? ""}
        </Alert>
      </Snackbar>
    </>
  );
}
